import { useCallback, useEffect, useRef } from 'react';
import { otcSessionRecordingUpload } from '@/lib/otcApi';
import { openSessionMedia, startSessionRecorder } from '@/lib/sessionRecording';

/**
 * Continuous MCQ/Tech session recorder.
 * Returns structured upload results so callers can gate SEB exit and offer retry.
 * Retains the stopped blob until upload succeeds or caller clears it.
 */
export function useSessionRecording(otc) {
  const recorderRef = useRef(null);
  const startingRef = useRef(false);
  const pendingBlobRef = useRef(null);

  const stopTracksOnly = useCallback(() => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec) return;
    try {
      rec.stopTracks();
    } catch { /* ignore */ }
  }, []);

  const clearPendingBlob = useCallback(() => {
    pendingBlobRef.current = null;
  }, []);

  const startRecording = useCallback(async () => {
    if (recorderRef.current || startingRef.current) return;
    startingRef.current = true;
    try {
      const stream = await openSessionMedia();
      recorderRef.current = startSessionRecorder(stream);
    } catch {
      // Camera denied / unsupported — assessment continues without recording.
    } finally {
      startingRef.current = false;
    }
  }, []);

  /** Stop MediaRecorder and retain blob for upload. Safe to call before terminal network I/O. */
  const prepareRecordingBlob = useCallback(async () => {
    if (pendingBlobRef.current) return pendingBlobRef.current;
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (!rec) return null;
    try {
      const result = await rec.stop();
      try {
        rec.stopTracks();
      } catch { /* ignore */ }
      if (!result?.blob) return null;
      pendingBlobRef.current = {
        blob: result.blob,
        contentType: result.contentType,
        durationSec: result.durationSec,
      };
      return pendingBlobRef.current;
    } catch {
      try {
        rec.stopTracks();
      } catch { /* ignore */ }
      return null;
    }
  }, []);

  /**
   * Upload session recording (best-effort). Keeps blob for retry on failure.
   * @returns {{ ok: boolean, started: boolean, error?: string }}
   */
  const stopAndUpload = useCallback(async (opts = {}) => {
    try {
      const captured = await prepareRecordingBlob();
      if (!captured) {
        return { ok: true, started: false };
      }
      await otcSessionRecordingUpload(
        otc,
        captured.blob,
        captured.contentType,
        captured.durationSec,
        {
          timeoutMs: opts.timeoutMs,
          onProgress: opts.onProgress,
        },
      );
      pendingBlobRef.current = null;
      return { ok: true, started: true };
    } catch (e) {
      return {
        ok: false,
        started: true,
        error: e?.message || 'upload_failed',
      };
    }
  }, [otc, prepareRecordingBlob]);

  useEffect(() => () => {
    stopTracksOnly();
    pendingBlobRef.current = null;
  }, [stopTracksOnly]);

  return {
    startRecording,
    prepareRecordingBlob,
    stopAndUpload,
    stopTracksOnly,
    clearPendingBlob,
  };
}
